/**
 * CL-CHK-01 — "the seller's payout setup is incomplete", recognised from Sharetribe's error rows.
 *
 * A seller who STARTED Stripe onboarding and never finished it has a Stripe account, so
 * Sharetribe does not refuse with `transaction-missing-stripe-account`. It runs
 * `stripe-create-payment-intent`, Stripe refuses, and the row is a 402
 * `transaction-payment-failed` whose only tell is Stripe's own message. The template recognises
 * two such messages in CheckoutPage/ErrorMessages.js and src/util/errors.js
 * (`destinationAccountNotCompleteStripeError`, `isTransactionChargeDisabledError`) and tells the
 * buyer to contact support. To the buyer it is the same problem as no payout account at all, with
 * the same fix, so every checkout shows the same seller-payout message for it.
 */
import { ERROR_CODE_PAYMENT_FAILED } from '../types';

// The normalised code the basket and offer checkouts give this case (their ducks keep only
// a code, and the row's own code is the generic payment-failed one).
export const SELLER_PAYOUT_INCOMPLETE = 'seller-payout-incomplete';

// Same two recognitions the template makes; Stripe gives no code for either.
const INCOMPLETE_ACCOUNT_MESSAGE =
  'Your destination account needs to have at least one of the following capabilities enabled';
const CHARGES_DISABLED_MESSAGE = 'Your account cannot currently make charges.';
const VERIFICATION_DISABLED = /verification.disabled_reason/;

/**
 * @param {Object} apiError one row of a Sharetribe API error (`data.errors[i]` / `apiErrors[i]`)
 * @returns {boolean} true when Stripe refused because the seller's account is not finished
 */
export const isSellerPayoutIncompleteApiError = apiError => {
  if (apiError?.status !== 402 || apiError?.code !== ERROR_CODE_PAYMENT_FAILED) {
    return false;
  }
  const message = apiError?.meta?.stripeMessage;
  return (
    typeof message === 'string' &&
    (message.includes(INCOMPLETE_ACCOUNT_MESSAGE) ||
      message.startsWith(CHARGES_DISABLED_MESSAGE) ||
      VERIFICATION_DISABLED.test(message))
  );
};

/**
 * @param {Object} error a storable error, as the CheckoutPage duck keeps it (`apiErrors`)
 * @returns {boolean}
 */
export const isSellerPayoutIncompleteError = error =>
  (error?.apiErrors || []).some(isSellerPayoutIncompleteApiError);
